// Theme registry — ported from discover's src/theme/tokens.ts (antd bridge
// dropped: discover-chat uses no Ant Design). Adding a theme = one entry here
// plus one [data-theme] block in theme.css.

export const DARK_THEME_IDS = ['dark', 'midnight', 'ocean', 'ember'];

export const THEMES = [
  { id: 'light', label: 'Light', swatch: ['#ffffff', '#3b6cff'] },
  { id: 'dark', label: 'Dark', swatch: ['#22252b', '#6f8dff'] },
  { id: 'midnight', label: 'Midnight', swatch: ['#0a0d16', '#7c6bff'] },
  { id: 'solar', label: 'Solar', swatch: ['#faf7f2', '#d96f32'] },
  { id: 'forest', label: 'Forest', swatch: ['#ffffff', '#16a34a'] },
  { id: 'ocean', label: 'Ocean', swatch: ['#0d1b2a', '#38bdf8'] },
  { id: 'sakura', label: 'Sakura', swatch: ['#faf6f8', '#ec4899'] },
  { id: 'ember', label: 'Ember', swatch: ['#1c1917', '#fb923c'] },
];

// Ink for a theme *preview* (the cards in Settings). An entry carries only
// [surface, accent] and no foreground value, so a preview's text bars need
// their tone derived from whether the previewed theme is a dark one — it
// describes that card's own contents, not the theme currently applied. Lives
// here beside the swatches because this module is where literal colour values
// belong; a JSX file reads it as data.
export const previewInk = (id) =>
  DARK_THEME_IDS.includes(id)
    ? { bar: 'rgba(255, 255, 255, 0.30)', edge: 'rgba(255, 255, 255, 0.16)' }
    : { bar: 'rgba(15, 23, 42, 0.20)', edge: 'rgba(15, 23, 42, 0.10)' };

// The PDF export (utils/answerExport.js) printed in each theme. theme.css's
// tokens flattened to opaque hex — pdfmake paints no alpha, so a translucent
// surface there is composited over its theme's own page colour here. accentInk
// is the accent shade that holds 4.5:1 as text on `paper`; accent itself is
// for fills and rules.
export const PDF_PALETTES = {
  light: { paper: '#ffffff', surface: '#f6f7f9', sunken: '#eef0f3', ink: '#16181d', inkSoft: '#5b6271', muted: '#6f7685', rule: '#e3e6ec', accent: '#3b6cff', accentInk: '#2f5be0', accentSoft: '#ebf0ff', onAccent: '#ffffff' },
  dark: { paper: '#1b1d22', surface: '#22252b', sunken: '#2a2d34', ink: '#f2f3f5', inkSoft: '#a5abb8', muted: '#8a909c', rule: '#30333a', accent: '#6f8dff', accentInk: '#8ea5ff', accentSoft: '#262c40', onAccent: '#0e0f12' },
  midnight: { paper: '#0f1322', surface: '#161b2e', sunken: '#1e2440', ink: '#eef1fb', inkSoft: '#9aa6c8', muted: '#7d89ab', rule: '#252c45', accent: '#7c6bff', accentInk: '#a397ff', accentSoft: '#211f48', onAccent: '#ffffff' },
  solar: { paper: '#fffdf9', surface: '#faf7f2', sunken: '#f1ece3', ink: '#2a2722', inkSoft: '#6e6759', muted: '#7e7564', rule: '#ebe4d8', accent: '#d96f32', accentInk: '#a8501f', accentSoft: '#fbeadf', onAccent: '#ffffff' },
  forest: { paper: '#ffffff', surface: '#f4f7f5', sunken: '#e9efeb', ink: '#15201a', inkSoft: '#566159', muted: '#6a766e', rule: '#e1e8e3', accent: '#16a34a', accentInk: '#15803d', accentSoft: '#e3f4ea', onAccent: '#ffffff' },
  ocean: { paper: '#0d1b2a', surface: '#13243a', sunken: '#1a2e47', ink: '#e8f2fb', inkSoft: '#93aec5', muted: '#7d98b0', rule: '#1f3550', accent: '#38bdf8', accentInk: '#5ccbfa', accentSoft: '#123049', onAccent: '#062033' },
  sakura: { paper: '#fffdfe', surface: '#faf6f8', sunken: '#f3e9ee', ink: '#231a1f', inkSoft: '#6d5a64', muted: '#806d77', rule: '#efe3e9', accent: '#ec4899', accentInk: '#be185d', accentSoft: '#fde7f2', onAccent: '#ffffff' },
  ember: { paper: '#1c1917', surface: '#25211d', sunken: '#2f2a25', ink: '#f5f1ec', inkSoft: '#b3a99e', muted: '#978d82', rule: '#352f29', accent: '#fb923c', accentInk: '#fdab6a', accentSoft: '#3a2618', onAccent: '#1a0f05' },
};

// Categorical series colours for the PDF's charts (one hue per category, in
// this fixed order — the order is what keeps neighbours apart under colour-
// blindness). The dataviz reference palette, validated against every theme's
// paper: light steps for light papers, dark steps for dark ones.
export const PDF_SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};

export const THEME_IDS = THEMES.map((t) => t.id);
export const DEFAULT_THEME = 'midnight';
export const STORAGE_KEY = 'theme';
